import { Controller } from "@hotwired/stimulus"

// Search inputs whose debounced submit was still pending when their controller disconnected.
// A WeakSet rather than a data attribute: Turbo's snapshot cache clones the DOM, and a cloned
// marker would fire a stray search on a restore visit.
const pendingSearchInputs = new WeakSet()

// Handles search functionality for the unified toolbar component.
// Provides debounced search input with clear button.
//
// Focus/cursor/value are preserved across frame refreshes natively: the search
// input carries `data-turbo-permanent`, so Turbo keeps the same DOM node when
// the surrounding turbo-frame is replaced. No JS focus-restoration is needed.
//
// When the toolbar itself sits inside that frame, every response replaces this controller's
// element while the input survives. A search still waiting out its debounce at that moment is
// handed over to the new controller instead of being dropped, or the list would show the
// results for an older term than the one in the field.
export default class extends Controller {
  static targets = ["input", "form", "clearButton"]
  static values = {
    debounceMs: { type: Number, default: 500 },
  }

  connect() {
    this.timeout = null
    this.updateClearButtonVisibility()
  }

  // The search input is data-turbo-permanent: during a Turbo render the node is
  // detached and re-inserted, so the controller can connect before the input
  // exists. Re-sync the clear button once the input (re-)attaches.
  inputTargetConnected(input) {
    this.updateClearButtonVisibility()
    if (pendingSearchInputs.delete(input)) this.search()
  }

  disconnect() {
    if (this.timeout && this.pendingInput) pendingSearchInputs.add(this.pendingInput)
    this.cancelPendingSearch()
  }

  search() {
    clearTimeout(this.timeout)
    // Kept by reference: during a Turbo render the permanent input may already be detached when
    // disconnect() runs, so it is no longer reachable as a target there.
    this.pendingInput = this.inputTarget
    this.timeout = setTimeout(() => {
      this.timeout = null
      this.formTarget.requestSubmit()
    }, this.debounceMsValue)
    this.updateClearButtonVisibility()
  }

  cancelPendingSearch() {
    clearTimeout(this.timeout)
    this.timeout = null
  }

  clear() {
    this.cancelPendingSearch()
    this.inputTarget.value = ""
    this.updateClearButtonVisibility()
    this.formTarget.requestSubmit()
  }

  updateClearButtonVisibility() {
    if (!this.hasClearButtonTarget || !this.hasInputTarget) return

    const hasValue = this.inputTarget.value.length > 0
    this.clearButtonTarget.classList.toggle("hidden", !hasValue)
  }

  // Handle enter key
  submitOnEnter(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.cancelPendingSearch()
      this.formTarget.requestSubmit()
    }
  }
}
